import React, { useContext, useState } from "react";
import { AxiosError } from "axios";
import { QueryKey, QueryResult, queryCache, useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import { ApiError } from "fe-shared/src/types/common";
import {
  PickupRequestDetails as PickupRequestDetailsModel,
  PickupRequestStatus,
} from "fe-shared-requests/src/types";
import { getDocuments } from "fe-shared-requests/src/api/pickupRequests";
import {
  EditAssignmentDialog,
  PickupRequestDetails,
  PickupRequestDialog,
  PickupRequestSendButton,
} from "fe-shared-requests/src/components";
import {
  canEditAssignment,
  canResendPickupRequest,
  canSendPickupRequest,
} from "fe-shared-requests/src/helpers/pickupRequestStatus";

import { IconButton } from "fe-shared/src/components";
import { EditIcon } from "fe-shared/src/components/Icons";
import { PermissionLevel, UserPermission } from "fe-shared/src/types/domain";
import {
  hasAnyPermission,
  hasPermission,
} from "fe-shared/src/helpers/permissions";
import useDialog from "fe-shared/src/hooks/useDialog";

import UserContext from "contexts/userContext";
import PickupRequestApproveButton from "components/PickupRequestApproveButton";
import PickupRequestFlagButton from "components/PickupRequestFlagButton";
import PickupRequestForceCompleteButton from "components/PickupRequestForceCompleteButton";
import PickupRequestInvalidateButton from "components/PickupRequestInvalidateButton";
import PickupRequestLinkTransactionButton from "components/PickupRequestLinkTransactionButton";
import PickupRequestStatusHeader from "components/PickupRequestStatusHeader";
import PickupRequestTransactionLink from "components/PickupRequestTransactionLink";

import {
  PERMISSIONS,
  REQUESTS_QUERY_KEY,
  REQUEST_ACTIVITIES_QUERY_KEY,
} from "consts";

const EDITABLE_STATUSES: PickupRequestStatus[] = [
  PickupRequestStatus.InReview,
  PickupRequestStatus.Approved,
  PickupRequestStatus.Requested,
];

const INVALIDATABLE_STATUSES: PickupRequestStatus[] = [
  PickupRequestStatus.InReview,
  PickupRequestStatus.Approved,
  PickupRequestStatus.Requested,
  PickupRequestStatus.Accepted,
];

interface Props {
  returnUrl: string;
  requestNumber: string;
  request: QueryResult<PickupRequestDetailsModel, AxiosError<ApiError>>;
  navigation?: {
    numbers: string[];
    total: number;
    onNeedMore?: () => void;
  };
}

const RequestDetailsView = (props: Props) => {
  const { returnUrl, requestNumber, request, navigation } = props;
  const { data } = request;
  const { t } = useTranslation();
  const currentUser = useContext(UserContext);
  const isPermitted = (permission: UserPermission) =>
    hasPermission(currentUser?.permissions, permission);

  // Documents sit on another feature subtree; their 403 is turned into a forced sign-out, so a
  // role without it must not issue the call at all.
  const canViewDocuments = isPermitted({
    name: PERMISSIONS.requestDocuments,
    level: PermissionLevel.Read,
  });
  const canManageDocuments = isPermitted({
    name: PERMISSIONS.requestDocuments,
    level: PermissionLevel.Full,
  });
  const canViewHistory = isPermitted({
    name: PERMISSIONS.requestHistory,
    level: PermissionLevel.Read,
  });
  const canExportHistory = isPermitted({
    name: PERMISSIONS.requestHistoryExport,
    level: PermissionLevel.Full,
  });
  const canViewNotes = isPermitted({
    name: PERMISSIONS.requestNotes,
    level: PermissionLevel.Read,
  });
  const canAddNote = isPermitted({
    name: PERMISSIONS.requestAddNote,
    level: PermissionLevel.Full,
  });
  const canAssign = hasAnyPermission(currentUser?.permissions, [
    { name: PERMISSIONS.requestListAssign, level: PermissionLevel.Full },
    { name: PERMISSIONS.requestDetailsAssign, level: PermissionLevel.Full },
  ]);
  const canApprove = isPermitted({
    name: PERMISSIONS.requestApprove,
    level: PermissionLevel.Full,
  });
  const canSend = isPermitted({
    name: PERMISSIONS.requestSend,
    level: PermissionLevel.Full,
  });
  const canResend = isPermitted({
    name: PERMISSIONS.requestResend,
    level: PermissionLevel.Full,
  });
  const canInvalidate = isPermitted({
    name: PERMISSIONS.requestInvalidate,
    level: PermissionLevel.Full,
  });
  const canEdit = isPermitted({
    name: PERMISSIONS.requestEdit,
    level: PermissionLevel.Full,
  });
  const canFlag = isPermitted({
    name: PERMISSIONS.requestFlag,
    level: PermissionLevel.Full,
  });
  const canForceComplete = isPermitted({
    name: PERMISSIONS.requestForceComplete,
    level: PermissionLevel.Full,
  });
  const canViewTransactionDetails = isPermitted({
    name: PERMISSIONS.transactionDetails,
    level: PermissionLevel.Read,
  });
  const canViewParticipant = isPermitted({
    name: PERMISSIONS.participantProfile,
    level: PermissionLevel.Read,
  });

  // Same key the shared component fetches documents under - react-query dedupes by key, so this
  // subscribes to its cache instead of issuing a second request, exactly like the reasons query
  // PickupRequestDialog and the details page already share.
  const documentsQueryKey: QueryKey = ["pickupRequestDocuments", requestNumber];
  const documentsQuery = useQuery(
    documentsQueryKey,
    () => getDocuments(requestNumber),
    { enabled: !!data && canViewDocuments }
  );

  const {
    isDialogActive: isEditDialogActive,
    isDialogRendered: isEditDialogRendered,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog();
  const [editDialogInstanceKey, setEditDialogInstanceKey] = useState(0);

  const onEditButtonClicked = () => {
    setEditDialogInstanceKey((key) => key + 1);
    openEditDialog();
  };

  const {
    isDialogRendered: isAssignmentDialogRendered,
    openDialog: openAssignmentDialog,
    closeDialog: closeAssignmentDialog,
  } = useDialog();

  const canApproveNow =
    canApprove && data?.status === PickupRequestStatus.InReview;
  const canSendNow = canSend && canSendPickupRequest(data);
  const canResendNow = canResend && canResendPickupRequest(data);
  const canInvalidateNow =
    canInvalidate && !!data && INVALIDATABLE_STATUSES.includes(data.status);
  const isAssignee = !!data && data.assignedTo === currentUser?.userId;
  const canEditNow =
    canEdit &&
    isAssignee &&
    !!data &&
    EDITABLE_STATUSES.includes(data.status);
  const canFlagNow = canFlag && (!data?.assignedTo || isAssignee);
  const canEditAssignmentNow =
    canEdit && isAssignee && !!data && canEditAssignment(data.status, "portal");
  const canForceCompleteNow =
    canForceComplete &&
    !!data &&
    data.status !== PickupRequestStatus.Invalid &&
    data.status !== PickupRequestStatus.Completed;
  const canLinkTransactionNow =
    canForceComplete &&
    data?.status === PickupRequestStatus.Completed &&
    !(data?.hasLinkedTransaction ?? !!data?.linkedTransactionNumber);

  const activitiesQueryKey: QueryKey = [
    REQUEST_ACTIVITIES_QUERY_KEY,
    requestNumber,
  ];

  return (
    <PickupRequestDetails
      returnUrl={returnUrl}
      requestNumber={requestNumber}
      request={request}
      navigation={navigation}
      currentUserEmail={currentUser?.email || ""}
      canViewPriority
      canViewFlag
      canViewCarrier
      canViewReasons
      canViewDocuments={canViewDocuments}
      canManageDocuments={canManageDocuments}
      canViewHistory={canViewHistory}
      canExportHistory={canExportHistory}
      canViewNotes={canViewNotes}
      canAddNote={canAddNote}
      participantLinkTo={
        canViewParticipant
          ? (companyId) => `/participants/${companyId}`
          : undefined
      }
      titleSlot={
        (
          <>
            <PickupRequestTransactionLink
              kind="shipment"
              transactionNumber={data?.shipmentTransactionNumber}
              canOpenTransaction={canViewTransactionDetails}
            />
            <PickupRequestTransactionLink
              kind="pickup"
              transactionNumber={data?.linkedTransactionNumber}
              canOpenTransaction={canViewTransactionDetails}
            />
          </>
        )
      }
      flagSlot={
        (
          <PickupRequestFlagButton
            canFlag={canFlagNow}
            requestNumber={data?.requestNumber ?? requestNumber}
            isFlagged={data?.isFlagged ?? false}
            flagComment={data?.flagComment ?? null}
            showFlagToParticipants={data?.showFlagToParticipants ?? false}
          />
        )
      }
      flagEditSlot={
        (
          <PickupRequestFlagButton
            mode="edit"
            canFlag={canFlagNow}
            requestNumber={data?.requestNumber ?? requestNumber}
            isFlagged={data?.isFlagged ?? false}
            flagComment={data?.flagComment ?? null}
            showFlagToParticipants={data?.showFlagToParticipants ?? false}
          />
        )
      }
      assigneeSlot={
        data && (
          <PickupRequestStatusHeader
            canAssign={canAssign}
            requestNumber={data.requestNumber ?? requestNumber}
            status={data.status}
            assignedTo={data.assignedTo}
            assignedToFullName={data.assignedToFullName}
          />
        )
      }
      assignmentActionSlot={
        canEditAssignmentNow && (
          <>
            <IconButton
              hasTooltip
              onClick={openAssignmentDialog}
              id="request-edit-assignment-button"
              aria-label={t("pickupRequests.assignment.edit")}
            >
              <EditIcon fillColor={null} />
            </IconButton>
            {isAssignmentDialogRendered && data && (
              <EditAssignmentDialog
                request={data}
                callerRole="portal"
                onClose={closeAssignmentDialog}
                onChanged={() => {
                  queryCache.invalidateQueries(REQUESTS_QUERY_KEY);
                  queryCache.invalidateQueries(activitiesQueryKey);
                }}
                onSaved={() => {
                  queryCache.invalidateQueries(REQUESTS_QUERY_KEY);
                  queryCache.invalidateQueries(activitiesQueryKey);
                  closeAssignmentDialog();
                }}
              />
            )}
          </>
        )
      }
      headerActionsSlot={
        canEditNow && (
          <>
            <IconButton
              hasTooltip
              onClick={onEditButtonClicked}
              id="request-edit-button"
              aria-label={t("pickupRequests.details.edit")}
            >
              <EditIcon fillColor={null} />
            </IconButton>
            {isEditDialogRendered && (
              <PickupRequestDialog
                key={editDialogInstanceKey}
                mode="edit"
                request={data}
                requestNumber={data?.requestNumber ?? requestNumber}
                existingDocuments={documentsQuery.data ?? []}
                canManageDocuments={canManageDocuments}
                requestsQueryKey={REQUESTS_QUERY_KEY}
                isActive={isEditDialogActive}
                onClose={closeEditDialog}
                onSuccess={() => {
                  queryCache.invalidateQueries([
                    REQUESTS_QUERY_KEY,
                    requestNumber,
                  ]);
                  queryCache.invalidateQueries(activitiesQueryKey);
                  queryCache.invalidateQueries(documentsQueryKey);
                }}
              />
            )}
          </>
        )
      }
      actionsSlot={
        (canApproveNow ||
          canSendNow ||
          canResendNow ||
          canInvalidateNow ||
          canForceCompleteNow ||
          canLinkTransactionNow) && (
          <>
            {canApproveNow && (
              <PickupRequestApproveButton
                requestNumber={data?.requestNumber ?? requestNumber}
              />
            )}
            {(canSendNow || canResendNow) && (
              <PickupRequestSendButton
                isResend={canResendNow && !canSendNow}
                requestNumber={data?.requestNumber ?? requestNumber}
                onSuccess={() => {
                  queryCache.invalidateQueries(REQUESTS_QUERY_KEY);
                  queryCache.invalidateQueries(activitiesQueryKey);
                }}
              />
            )}
            {canForceCompleteNow && (
              <PickupRequestForceCompleteButton
                requestNumber={data?.requestNumber ?? requestNumber}
              />
            )}
            {canInvalidateNow && (
              <PickupRequestInvalidateButton
                requestNumber={data?.requestNumber ?? requestNumber}
              />
            )}
            {canLinkTransactionNow && (
              <PickupRequestLinkTransactionButton
                requestNumber={data?.requestNumber ?? requestNumber}
                collectorName={data?.source?.displayName ?? ""}
                materials={data?.materials ?? []}
              />
            )}
          </>
        )
      }
    />
  );
};

export default RequestDetailsView;
